/**
 * Engagement creation form schema — Phase 6a.9.
 *
 * Frontend-side validation (form-level). The backend has its own Zod
 * schema in `engagementInputSchema.ts` (Backend Spec §20.3); the frontend
 * intentionally OMITS the 18-month max audit-period check per Ömer's
 * directive — auditors should be able to enter long periods and let the
 * backend reject them so the error message is auditor-actionable.
 *
 * Conditional refinement: when `openingBalanceMode === "MANUAL"`, the
 * `openingBalances` block becomes required. AUTO mode leaves it
 * undefined.
 */

import { z } from "zod";

import { OPENING_BALANCE_MODES } from "@/types/engagement";

// Phase 6e.4.1 — widened from 2 to 6 fractional digits.
// Phase 6a.20g — widened further from 6 to 10 fractional digits.
// TCMB cross-rates for low-value currencies (e.g. KRW) can reach
// 8 decimal places (e.g. `0.00068898`). 10 = 8 + 2 buffer, still
// tight enough to reject accidental paste of arbitrary-precision
// rates from custom systems.
const decimalString = z
  .string()
  .regex(/^\d+(\.\d{1,10})?$/, "validation.invalidNumber");

export const engagementCreationSchema = z
  .object({
    clientName: z
      .string()
      .min(2, "validation.clientNameTooShort")
      .max(100, "validation.clientNameTooLong"),
    auditPeriodStart: z.date({
      required_error: "validation.auditPeriodStartRequired",
    }),
    auditPeriodEnd: z.date({
      required_error: "validation.auditPeriodEndRequired",
    }),
    functionalCurrency: z.enum(["TRY", "USD", "EUR", "GBP"]),
    // Phase 6c.2 (Frontend Spec §21.6.2) — 3-value canonical enum; no
    // default. The form starts the radio group with no selection
    // (modeled as `undefined` in the form state) and Zod rejects the
    // submission until the auditor picks a mode. `super_refine` below
    // upgrades the optional field into a hard requirement at submit
    // time so react-hook-form can still bind to a controlled value.
    openingBalanceMode: z.enum(OPENING_BALANCE_MODES).optional(),
    /**
     * Mini-fix 6a.10/F-1 (Spec §21.7.8 — Slot Management). Captures the
     * number of contracts the auditor expects in this engagement so the
     * contracts list starts with N pre-allocated `null` slots (checklist
     * effect). Append-only "+ Yeni Sözleşme Ekle" remains for late
     * additions beyond N.
     */
    contractCount: z.coerce
      .number({
        required_error: "validation.contractCountRequired",
        invalid_type_error: "validation.invalidNumber",
      })
      .int("validation.contractCountInteger")
      .min(1, "validation.contractCountMin")
      .max(500, "validation.contractCountMax"),
    openingBalances: z
      .object({
        rouAssetCost: decimalString,
        rouAccumulatedDepreciation: decimalString,
        leaseLiability: decimalString,
      })
      .optional(),
    /**
     * Phase 6a.23 — engagement-level closing FX rates, keyed by
     * currency code (e.g. `"USD"`, `"EUR"`). Stored as
     * positive-decimal strings to preserve precision (auditors paste
     * `35.50000` from TCMB and `parseFloat` would silently trim
     * trailing zeros). The map MAY contain entries for currencies
     * that no contract uses — the engagement-form section renders
     * one row per derived FX currency, but if the user soft-deletes
     * the last USD contract, the USD rate is left in place rather
     * than auto-cleared (audit-trail principle: never silently drop
     * auditor-entered data).
     *
     * The CALCULATE-time requirement — "every currency used by a
     * non-soft-deleted contract must have a rate here" — is enforced
     * by the backend (and surfaced as a per-contract rejection); the
     * frontend's section also blocks submission when a derived
     * currency row is left blank.
     */
    closingFxRates: z.record(z.string(), decimalString).optional(),
  })
  .refine((data) => data.auditPeriodEnd > data.auditPeriodStart, {
    message: "validation.auditPeriodEndAfterStart",
    path: ["auditPeriodEnd"],
  })
  .refine(
    (data) => {
      if (data.openingBalanceMode === "MANUAL") {
        return data.openingBalances !== undefined;
      }
      return true;
    },
    {
      message: "validation.openingBalancesRequired",
      path: ["openingBalances"],
    },
  )
  .superRefine((data, ctx) => {
    // Phase 6c.2 (Frontend Spec §21.6.2) — submit-time guard for the
    // "no default selection" rule: the radio group starts unset and
    // Zod must reject the form until the auditor picks a mode.
    if (!data.openingBalanceMode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "form.openingBalanceMode.required",
        path: ["openingBalanceMode"],
      });
    }
  });

export type EngagementCreationFormValues = z.infer<
  typeof engagementCreationSchema
>;
